import React from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ComingSoonPlaceholder } from "./common/ComingSoonPlaceholder";
import { PdfConverterContent } from "./PdfConverterContent";

export interface ToolDefinition {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  kind: "route" | "modal" | "coming_soon";
  path?: string;
}

interface ToolModalProps {
  open: boolean;
  onClose: () => void;
  tool: ToolDefinition | null;
}

const renderToolContent = (tool: ToolDefinition) => {
  if (tool.kind === "modal") {
    switch (tool.id) {
      case "pdf_converter":
        return <PdfConverterContent />;
      default:
        return <ComingSoonPlaceholder toolName={tool.name} toolColor={tool.color} toolIcon={tool.icon} />;
    }
  }
  return <ComingSoonPlaceholder toolName={tool.name} toolColor={tool.color} toolIcon={tool.icon} />;
};

export const ToolModal: React.FC<ToolModalProps> = ({ open, onClose, tool }) => {
  if (!tool) return null;

  const preventBrowserFileDrop = (e: React.DragEvent) => {
    if (tool.id !== "pdf_converter") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const isComingSoon = tool.kind === "coming_soon";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      onDragOver={preventBrowserFileDrop}
      onDrop={preventBrowserFileDrop}
      PaperProps={{
        sx: {
          width: "92vw",
          height: "90vh",
          maxWidth: "none",
          borderRadius: "1.5rem",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            bgcolor: tool.color + "18",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: tool.color,
            flexShrink: 0,
            "& svg": { fontSize: "1.5rem" },
          }}
        >
          {tool.icon}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {tool.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {tool.description}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "text.secondary", "&:hover": { bgcolor: "action.hover" } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          bgcolor: "#F7F8FA",
          p: 4,
          overflowY: "auto",
        }}
      >
        {renderToolContent(tool)}
      </DialogContent>
    </Dialog>
  );
};
